import React, { useCallback, useState, useRef } from 'react';
import { UploadIcon, FileSpreadsheetIcon, XIcon } from 'lucide-react';
interface FileUploadProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  disabled?: boolean;
}
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
export function FileUpload({
  file,
  onFileSelect,
  onFileRemove,
  disabled = false
}: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragOver(true);
  }, [disabled]);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (disabled) return;
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      onFileSelect(droppedFile);
    }
  }, [disabled, onFileSelect]);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onFileSelect]);
  const handleBrowseClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);
  const isExcel = file?.name.endsWith('.xlsx') || file?.name.endsWith('.xls');
  if (file) {
    return <div className="bg-white rounded-lg border border-gray-200 p-5" data-id="element-40">
        <div className="flex items-center justify-between" data-id="element-41">
          <div className="flex items-center gap-4" data-id="element-42">
            <div className="flex-shrink-0 w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center" data-id="element-43">
              <FileSpreadsheetIcon className="w-6 h-6 text-green-600" aria-hidden="true" data-id="element-44" />
            </div>
            <div data-id="element-45">
              <p className="text-sm font-medium text-gray-900" data-id="element-46">{file.name}</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-47">
                {formatFileSize(file.size)} · {file.type || 'text/csv'}
              </p>
            </div>
          </div>
          {!disabled && <button type="button" onClick={onFileRemove} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-300" aria-label="Remove selected file" data-id="element-48">
              <XIcon className="w-5 h-5" aria-hidden="true" data-id="element-49" />
            </button>}
        </div>
        {isExcel && <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-50">
            <p className="text-xs text-amber-700" data-id="element-51">
              <strong data-id="element-52">Note:</strong> CSV format is recommended for best results.
              Excel parsing may be limited in the browser.
            </p>
          </div>}
      </div>;
  }
  return <div data-id="element-53">
      <div role="button" tabIndex={0} aria-label="Upload snapshot file. Drag and drop or click to browse." className={`
          relative border-2 border-dashed rounded-lg p-10 text-center cursor-pointer
          transition-all duration-200
          ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={handleBrowseClick} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleBrowseClick();
      }
    }} data-id="element-54">
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleInputChange} className="sr-only" aria-hidden="true" tabIndex={-1} data-id="element-55" />
        <div className="flex flex-col items-center gap-3" data-id="element-56">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isDragOver ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-57">
            <UploadIcon className={`w-7 h-7 transition-colors ${isDragOver ? 'text-blue-600' : 'text-gray-400'}`} aria-hidden="true" data-id="element-58" />
          </div>
          <div data-id="element-59">
            <p className="text-sm font-medium text-gray-700" data-id="element-60">
              {isDragOver ? 'Drop your file here' : 'Drag & drop your snapshot file'}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-61">
              or <span className="text-blue-600 font-medium" data-id="element-62">browse files</span>
            </p>
          </div>
          <p className="text-xs text-gray-400" data-id="element-63">Supports CSV and Excel files</p>
        </div>
      </div>
    </div>;
}